import React from 'react'
import S from './styled'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import cx from 'classnames'
import { hasRefTo, hasRef1From, hasRef2From } from './colors.js'

import { ConfirmModal } from '../modal'

import { HamburgerGray, SmallPlus, CurvedArrow, DeleteIcon, ShevronRight } from '../icons'

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)

  return result
}

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: 'none',
  outline: 'none',
  background: isDragging ? 'transparent' : 'white',
  padding: isDragging ? '20px 0' : 0,
  marginTop: isDragging ? '-20px' : 0,

  // styles we need to apply on draggables
  ...draggableStyle,
})

const getListStyle = isDraggingOver => ({
  padding: isDraggingOver ? '20px' : 0,
  border: isDraggingOver ? '1px dashed #ddd' : 'none',
  borderRadius: isDraggingOver ? '6px' : 'none',
  transition: 'padding .1s ease-in-out',
})

class Category extends React.Component{

  state = {
    itemToDelete: null,
  }

  setShowDeleteModal = deleteInfo => this.setState({ itemToDelete: deleteInfo })

  onDragEnd = (result, typePath) => {
    // dropped outside the list
    if (!result.destination) {
      return
    }

    const items = reorder(
      this.props.steps,
      result.source.index,
      result.destination.index,
    )

    this.props.onChangeOrder(typePath, items, result.source.index, result.destination.index)
  }

  render () {
    const { type, title, options, steps, appliance, selected, onSelect, onAdd, onDelete, colors, onChangeOrder, readOnly } = this.props

    const typePath = `applianceSteps.${appliance.id}.${type}Steps`

    const prefix = itemIndex => `${typePath}[${itemIndex}]`
    const replacementPrefix = (itemIndex, replacementIndex) => `${typePath}[${itemIndex}].replacements[${replacementIndex}]`

    return (
      <React.Fragment>
        <S.ListItem className="category">
          {title}
        </S.ListItem>
        <DragDropContext onDragEnd={result => this.onDragEnd(result, typePath)}>
          <Droppable droppableId={`droppable_${type}`} isDropDisabled={!onChangeOrder || readOnly}>
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver)}
              >
                {
                  steps.map((step, index) => (
                    <Draggable
                      key={`item_${options.getId(step)}`}
                      draggableId={`item_${options.getId(step)}`}
                      index={index}
                      isDragDisabled={!onChangeOrder || readOnly}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={getItemStyle(
                            snapshot.isDragging,
                            provided.draggableProps.style,
                          )}
                        >
                          <S.ListItem
                            className={cx({
                              subcategory: true,
                              selected: prefix(index) === selected,
                              error: !options.isValidPath(prefix(index)),
                            })}
                            onClick={() => {
                              onSelect && onSelect(prefix(index))
                            }}
                          >
                            { onChangeOrder ? <HamburgerGray /> : <S.Placeholder />}
                            <S.ItemInfo>
                              <S.ItemInfoTitle>
                                {options.getItemTitle(step)}
                              </S.ItemInfoTitle>
                              <S.ItemInfoDescription>
                                {options.getItemDescription(step)}
                              </S.ItemInfoDescription>
                            </S.ItemInfo>
                            {
                              !readOnly && onDelete && prefix(index) === selected && (
                                <S.Delete onClick={() =>
                                  this.setShowDeleteModal({
                                    item: step,
                                    index,
                                    typePath,
                                  })
                                }>
                                  <DeleteIcon />
                                </S.Delete>
                              )
                            }
                            <S.Refs>
                              {
                                hasRef1From(step, colors) && (
                                  <S.RefFrom style={{ color: hasRef1From(step, colors) }}>
                                    <ShevronRight />
                                  </S.RefFrom>
                                )
                              }
                              {
                                hasRef2From(step, colors) && (
                                  <S.RefFrom style={{ color: hasRef2From(step, colors) }}>
                                    <ShevronRight />
                                  </S.RefFrom>
                                )
                              }
                            </S.Refs>
                            {
                              hasRefTo(step, colors) && <S.RefTo style={{ background: hasRefTo(step, colors) }} />
                            }
                          </S.ListItem>
                          {
                            step.replacements && step.replacements.map((replacement, replacementIndex) => (
                              <S.ListItem
                                key={`replacement_${options.getId(replacement)}`}
                                className={cx({
                                  subcategory: true,
                                  subchildren: true,
                                  selected: replacementPrefix(index, replacementIndex) === selected,
                                  error: !options.isValidPath(replacementPrefix(index, replacementIndex)),
                                })}
                                onClick={() => {
                                  onSelect && onSelect(replacementPrefix(index, replacementIndex))
                                }}
                              >
                                <CurvedArrow />
                                <S.ItemInfo className="subcategory">
                                  <S.ItemInfoTitle>
                                    {options.getItemTitle(replacement)}
                                  </S.ItemInfoTitle>
                                  <S.ItemInfoDescription>
                                    {options.getItemDescription(replacement)}
                                  </S.ItemInfoDescription>
                                </S.ItemInfo>
                              </S.ListItem>
                            ))
                          }
                        </div>
                      )
                      }

                    </Draggable>
                  ))
                }
                {provided.placeholder}
              </div>)}</Droppable></DragDropContext>
        { !readOnly && onAdd && (<S.ListItem className="addIcon" onClick={() => onAdd(type, typePath)}>
          <SmallPlus className="icon"/>
        </S.ListItem>)}
        {
          !!this.state.itemToDelete && (
            <ConfirmModal
              isOpened
              onClose={() => this.setShowDeleteModal(null)}
              onConfirm={() => {
                onDelete && onDelete(this.state.itemToDelete)
                this.setShowDeleteModal(null)
              }}
              redConfirm
            >
                Are you sure you want to delete this item?
            </ConfirmModal>
          )
        }
      </React.Fragment>
    )
  }
}

export default Category
